import React, { useMemo, useState } from 'react';
import Sym from './Sym';
import Modal from './Modal';
import { SearchInput } from './Field';
import { useCompany } from './CompanyContext';
import AlertService from '../../utils/alertService';

/**
 * Cambiar de empresa activa.
 *
 * El disparador NO es este componente: es el propio título del header (lo monta
 * AppShell). Antes había aquí un botón con el nombre de la empresa, y como el
 * título ya lo dice, el nombre salía dos veces —y en el botón, además,
 * truncado—. Ahora se toca el nombre y se abre esta ventana.
 *
 * Es un modal y no un menú porque la lista puede tener decenas de empresas: aquí
 * hay sitio para el RFC y el número de colaboradores de cada una, el buscador
 * recibe el foco solo y en el teléfono ocupa la pantalla en vez de un menú
 * apretado.
 */

export const nombreDeEmpresa = (c) => (
  c?.name || c?.businessName || c?.company_name || 'Empresa sin nombre'
);

const CompanySwitcher = ({ open, onClose }) => {
  const {
    empresas, empresaActiva, setEmpresaActiva, sincronizacionEnCurso,
  } = useCompany();
  const [busqueda, setBusqueda] = useState('');

  const filtradas = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return empresas;
    return empresas.filter((c) => (
      nombreDeEmpresa(c).toLowerCase().includes(q) || String(c?.rfc || '').toLowerCase().includes(q)
    ));
  }, [empresas, busqueda]);

  if (!open) return null;

  const elegir = async (c) => {
    // Con una descarga del SAT en curso se avisa antes de cambiar: al cambiar
    // se deja de ver el avance aquí. La solicitud NO se cancela —el SAT la sigue
    // procesando— y al volver a esa empresa la pantalla la retoma sola.
    if (
      sincronizacionEnCurso &&
      Number(c?.id) !== Number(sincronizacionEnCurso.idEmpresa)
    ) {
      const continuar = await AlertService.confirm(
        'Sincronización en curso',
        `${sincronizacionEnCurso.nombre} está descargando recibos del SAT. ` +
        'Si cambias de empresa dejarás de ver el avance; la descarga continúa ' +
        'y se retoma al volver a esa empresa. ¿Cambiar de todos modos?',
        { confirmText: 'Sí, cambiar de empresa', cancelText: 'Quedarme' },
      );
      if (!continuar) return;
    }

    setEmpresaActiva(c?.id);
    setBusqueda('');
    onClose?.();
  };

  return (
    <Modal
      open
      onClose={() => { setBusqueda(''); onClose?.(); }}
      title="Cambiar de empresa"
      subtitle={`${empresas.length} empresas`}
      maxWidth={520}
    >
      <SearchInput
        value={busqueda}
        onChange={(e) => setBusqueda(e?.target?.value)}
        placeholder="Buscar por nombre o RFC…"
        aria-label="Buscar empresa"
        autoFocus
        style={{ marginBottom: 14 }}
      />

      {filtradas.length === 0 ? (
        <p
          style={{
            font: "500 .9rem 'Hanken Grotesk'", color: 'var(--muted)',
            textAlign: 'center', padding: '24px 10px', margin: 0,
          }}
        >
          Ninguna empresa coincide con «{busqueda}».
        </p>
      ) : (
        <div style={{ maxHeight: '52vh', overflowY: 'auto', margin: '0 -4px', padding: '0 4px' }}>
          {filtradas.map((c) => {
            const activa = Number(c?.id) === Number(empresaActiva?.id);
            return (
              <button
                key={c?.id}
                type="button"
                className={`a2-empresa-opcion${activa ? ' a2-empresa-opcion--activa' : ''}`}
                onClick={() => elegir(c)}
                aria-current={activa ? 'true' : undefined}
              >
                <span
                  style={{
                    width: 36, height: 36, borderRadius: 10, flexShrink: 0, display: 'grid',
                    placeItems: 'center', background: 'var(--surface-2)',
                    border: '1px solid var(--line-2)', color: 'var(--brand-2)',
                  }}
                >
                  <Sym name="domain" size={19} />
                </span>

                <span style={{ flex: 1, minWidth: 0 }}>
                  <span className="a2-tcell-strong a2-truncate" style={{ display: 'block' }}>
                    {nombreDeEmpresa(c)}
                  </span>
                  <span className="a2-tcell-muted" style={{ display: 'block' }}>
                    <span className="a2-mono">{c?.rfc || 'Sin RFC'}</span>
                    {c?.employees?.length != null ? ` · ${c.employees.length} colaboradores` : ''}
                  </span>
                </span>

                {activa ? (
                  <Sym name="check_circle" size={20} style={{ color: 'var(--brand-2)', flexShrink: 0 }} />
                ) : null}
              </button>
            );
          })}
        </div>
      )}
    </Modal>
  );
};

export default CompanySwitcher;
